import React, { useState, useEffect } from 'react';
import OrigemBadge from '../../components/OrigemBadge';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AcoesDaLinha, BotaoVer, BotaoEditar, BotaoExcluir } from '../../components/BotoesAcao';
import { useConfirm } from '../../components/ConfirmDialog';
import { Abastecimento } from '../../types';
import { abastecimentosService } from '../../services/abastecimentos';
import ExportButtons from '../../components/ExportButtons';
import { exportService } from '../../services/export';
import { RespostaPaginada } from '../../types';
import { parseApiDate, formatKilometers } from '../../utils/formatters';
import { usePermissoes } from '../../hooks/usePermissoes';
import AvisoSemPermissao from '../../components/AvisoSemPermissao';
import { ehSemPermissao, mensagemDoErro } from '../../utils/erros';

const AbastecimentosList: React.FC = () => {
  const { pode } = usePermissoes();
  const [filters, setFilters] = useState({
    veiculo_id: undefined as number | undefined,
    motorista_id: undefined as number | undefined,
    tipo_combustivel: '',
    data_inicio: '',
    data_fim: '',
    cidade: '',
    tanque_cheio: undefined as boolean | undefined,
    sort_by: 'data_abastecimento',
    sort_order: 'desc',
    per_page: 15,
    page: 1
  });

  const navigate = useNavigate();
  const confirm = useConfirm();

  const { data, isLoading, error, refetch } = useQuery<RespostaPaginada<Abastecimento>>({
    queryKey: ['abastecimentos', filters],
    queryFn: () => abastecimentosService.getAbastecimentos(filters),
    placeholderData: (previousData) => previousData
  });

  const handleDelete = async (abastecimento: Abastecimento) => {
    const ok = await confirm({
      title: 'Excluir abastecimento',
      message: `Tem certeza que deseja excluir este abastecimento${abastecimento.veiculo ? ` do veículo ${abastecimento.veiculo.placa}` : ''}? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await abastecimentosService.deleteAbastecimento(abastecimento.id);
      toast.success('Abastecimento excluído com sucesso');
      refetch();
    } catch (err) {
      toast.error(mensagemDoErro(err, 'Erro ao excluir abastecimento'));
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    // Voltar para a primeira pagina ao mudar um filtro esta certo: o resultado
    // e outro e a pagina 5 do resultado anterior nao significa nada. Mas isso
    // nao pode valer quando a chave alterada E a pagina, senao o botao Proxima
    // pede a pagina 2 e a linha seguinte devolve para a 1 — a paginacao fica
    // inutilizada sem nenhum sinal de erro.
    setFilters(prev => ({ ...prev, [key]: value, ...(key === 'page' ? {} : { page: 1 }) }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getTipoCombustivelColor = (tipo: string) => {
    switch (tipo) {
      case 'gasolina': return 'bg-red-100 text-red-800';
      case 'etanol': return 'bg-green-100 text-green-800';
      case 'diesel': return 'bg-blue-100 text-blue-800';
      case 'gnv': return 'bg-purple-100 text-purple-800';
      case 'flex': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: string) => {
    const d = parseApiDate(date);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Tratar erros
  useEffect(() => {
    // Falta de permissão não vira aviso passageiro: a tela inteira explica o
    // que aconteceu, logo abaixo, e um toast só somaria ruído.
    if (error && !ehSemPermissao(error)) {
      toast.error(mensagemDoErro(error, 'Erro ao carregar abastecimentos'));
    }
  }, [error]);

  if (ehSemPermissao(error)) {
    return <AvisoSemPermissao erro={error} />;
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Abastecimentos</h1>
          <p className="text-gray-600">Controle de combustível dos veículos</p>
        </div>
        <div className="flex items-center gap-3">
          {pode('relatorios', 'exportar') && (
            <ExportButtons
            onExportPdf={() => exportService.abastecimentosPdf()}
            onExportExcel={() => exportService.abastecimentosExcel()}
          />
          )}
          {pode('abastecimentos', 'criar') && (
          <Link
            to="/abastecimentos/novo"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo abastecimento
          </Link>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Combustível</label>
            <select
              value={filters.tipo_combustivel}
              onChange={(e) => handleFilterChange('tipo_combustivel', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="gasolina">Gasolina</option>
              <option value="etanol">Etanol</option>
              <option value="diesel">Diesel</option>
              <option value="gnv">GNV</option>
              <option value="flex">Flex</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
            <input
              type="date"
              value={filters.data_inicio}
              onChange={(e) => handleFilterChange('data_inicio', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
            <input
              type="date"
              value={filters.data_fim}
              onChange={(e) => handleFilterChange('data_fim', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
            <input
              type="text"
              value={filters.cidade}
              onChange={(e) => handleFilterChange('cidade', e.target.value)}
              placeholder="Digite a cidade..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tanque Cheio</label>
            <select
              value={filters.tanque_cheio === undefined ? '' : filters.tanque_cheio.toString()}
              onChange={(e) => handleFilterChange('tanque_cheio', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Abastecimentos */}
      <div className="bg-white rounded-lg shadow-sm border">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando abastecimentos...</p>
          </div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Veículo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Motorista
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Combustível
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Litros
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Posto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comprovante
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.data.map((abastecimento: Abastecimento) => (
                    <tr key={abastecimento.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {formatDate(abastecimento.data_abastecimento)}
                          <OrigemBadge origem={abastecimento.origem} />
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatKilometers(abastecimento.quilometragem)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {abastecimento.veiculo?.placa}
                        </div>
                        <div className="text-sm text-gray-500">
                          {abastecimento.veiculo?.marca} {abastecimento.veiculo?.modelo}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {abastecimento.motorista?.nome}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoCombustivelColor(abastecimento.tipo_combustivel)}`}>
                          {abastecimento.tipo_combustivel}
                        </span>
                        {abastecimento.tanque_cheio && (
                          <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatNumber(abastecimento.litros)} L
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatCurrency(abastecimento.preco_litro)}/L
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(abastecimento.valor_total)}
                        </div>
                        {abastecimento.consumo && (
                          <div className="text-sm text-gray-500">
                            {formatNumber(abastecimento.consumo)} km/L
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {abastecimento.posto}
                        </div>
                        <div className="text-sm text-gray-500">
                          {abastecimento.cidade}, {abastecimento.estado}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(abastecimento.comprovantes?.length ?? 0) > 0 ? (
                          <div className="flex items-center gap-2">
                            {/* Leva ao detalhe, nao ao arquivo: com varios
                                comprovantes nao ha um "o" comprovante para abrir. */}
                            <button
                              type="button"
                              onClick={() => navigate(`/abastecimentos/${abastecimento.id}`)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Ver
                            </button>
                            <span className="text-xs text-gray-500">
                              {abastecimento.comprovantes!.length} arquivo(s)
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Sem comprovante</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <AcoesDaLinha>
                          <BotaoVer onClick={() => navigate(`/abastecimentos/${abastecimento.id}`)} title="Ver detalhes" />
                          {pode('abastecimentos', 'editar') && <BotaoEditar onClick={() => navigate(`/abastecimentos/${abastecimento.id}/editar`)} />}
                          {pode('abastecimentos', 'excluir') && <BotaoExcluir onClick={() => handleDelete(abastecimento)} />}
                        </AcoesDaLinha>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {data?.pagination && data.pagination.last_page > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page - 1)}
                    disabled={data.pagination.current_page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page + 1)}
                    disabled={data.pagination.current_page === data.pagination.last_page}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((data.pagination.current_page - 1) * data.pagination.per_page) + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(data.pagination.current_page * data.pagination.per_page, data.pagination.total)}
                      </span> de{' '}
                      <span className="font-medium">{data.pagination.total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page - 1)}
                        disabled={data.pagination.current_page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page + 1)}
                        disabled={data.pagination.current_page === data.pagination.last_page}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum abastecimento encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece registrando um novo abastecimento.
            </p>
            <div className="mt-6">
              <Link
                to="/abastecimentos/novo"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Novo abastecimento
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AbastecimentosList;
